// Word tokenisation for the chat "Save vocabulary" popup (VocabularyPopup).
//
// The popup lets a student drag across whole words, so the token boundaries
// decide the smallest thing they can capture. Space-delimited scripts split
// on a letters-only regex (apostrophes joined: "don't", digits never a word).
// Han / Hiragana / Katakana text, and Thai, Lao, Khmer and Burmese, have no
// spaces between words, so a regex run would be the whole clause — for those
// we ask Intl.Segmenter (dictionary-based ICU word breaks, present in every
// evergreen browser). Where it is missing, Han/Kana fall back to one token
// per character so any substring stays selectable; a South-East Asian run
// stays whole (a Thai letter is not a word).

export interface CaptureToken {
  text: string;
  isWord: boolean;
}

export type TokenRange = [number, number] | null;

// Scripts written without spaces between words: Han/Kana plus the four
// South-East Asian scripts ICU segments by dictionary (#768). Exported:
// chat/savedWords.ts rebuilds the dropped separator and must split on exactly
// the boundaries separatorFor joins with nothing.
export const UNSPACED_SCRIPT =
  /[\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}\p{Script=Thai}\p{Script=Lao}\p{Script=Khmer}\p{Script=Myanmar}]/u;
// Only Han/Kana make sense one character at a time when no segmenter exists.
const PER_CHARACTER_FALLBACK = /[\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}]/u;
// A letter carries its combining marks: Devanagari matras and viramas, Arabic
// harakat, a decomposed accent are \p{M}, not \p{L}, and a letters-only run
// split "नमस्ते" into consonant fragments a student could capture and pay for.
// A zero-width joiner / non-joiner (Persian می‌خواهم, a Hindi conjunct) is Cf,
// not \p{M}, and still binds two halves of one word.
const SPACED_WORD = /((?:\p{L}[\p{M}\u200C\u200D]*)+(?:['’](?:\p{L}[\p{M}\u200C\u200D]*)+)*)/gu;
const HAS_LETTER = /\p{L}/u;

export function tokenizeForCapture(text: string, languageCode?: string | null): CaptureToken[] {
  if (!UNSPACED_SCRIPT.test(text)) return tokenizeSpaced(text);
  const segmenter = wordSegmenter(languageCode);
  return segmenter ? tokenizeWithSegmenter(text, segmenter) : tokenizeUnspacedFallback(text);
}

// The text a selection of tokens stands for. Space-delimited words are joined
// with a single space (punctuation between them dropped, as before); adjacent
// CJK words are joined with nothing, so "最近|怎么|样" reads back as 最近怎么样.
export function selectionText(tokens: CaptureToken[], range: TokenRange): string {
  if (!range) return "";
  let out = "";
  let previousWordIndex = -1;
  for (let i = range[0]; i <= range[1]; i++) {
    const token = tokens[i];
    if (!token?.isWord) continue;
    if (previousWordIndex >= 0) {
      const between = tokens
        .slice(previousWordIndex + 1, i)
        .map((t) => t.text)
        .join("");
      out += separatorFor(tokens[previousWordIndex].text, token.text, between);
    }
    out += token.text;
    previousWordIndex = i;
  }
  return out;
}

function separatorFor(previous: string, next: string, between: string): string {
  if (between === "") return "";
  if (/\s/.test(between)) return " ";
  const cjkBoundary =
    UNSPACED_SCRIPT.test(previous.slice(-1)) && UNSPACED_SCRIPT.test([...next][0] ?? "");
  return cjkBoundary ? "" : " ";
}

function tokenizeSpaced(text: string): CaptureToken[] {
  const out: CaptureToken[] = [];
  let lastIndex = 0;
  for (const match of text.matchAll(SPACED_WORD)) {
    const start = match.index ?? 0;
    if (start > lastIndex) out.push({ text: text.slice(lastIndex, start), isWord: false });
    out.push({ text: match[0], isWord: true });
    lastIndex = start + match[0].length;
  }
  if (lastIndex < text.length) out.push({ text: text.slice(lastIndex), isWord: false });
  return out;
}

function wordSegmenter(languageCode?: string | null): Intl.Segmenter | null {
  if (typeof Intl === "undefined" || typeof Intl.Segmenter !== "function") return null;
  // ICU's CJK word breaks are dictionary-based regardless of locale; the tag is
  // only a hint, so a bad one (or none) must not take the feature down.
  try {
    return new Intl.Segmenter(languageCode || undefined, { granularity: "word" });
  } catch {
    try {
      return new Intl.Segmenter(undefined, { granularity: "word" });
    } catch {
      return null;
    }
  }
}

function tokenizeWithSegmenter(text: string, segmenter: Intl.Segmenter): CaptureToken[] {
  const out: CaptureToken[] = [];
  const push = (token: CaptureToken) => {
    const last = out[out.length - 1];
    if (!token.isWord && last && !last.isWord) {
      last.text += token.text;
    } else {
      out.push(token);
    }
  };
  for (const segment of segmenter.segment(text)) {
    // Letters only, like the regex path: a bare number is not vocabulary.
    if (segment.isWordLike && HAS_LETTER.test(segment.segment)) {
      for (const piece of splitWordSegment(segment.segment)) push(piece);
    } else {
      push({ text: segment.segment, isWord: false });
    }
  }
  return out;
}

const ZWNJ = String.fromCodePoint(0x200c);
const ZWJ = String.fromCodePoint(0x200d);
const COMBINING_MARK = /\p{M}/u;

// ICU glues digits, symbols and Latin letters onto a Thai/Lao/Khmer/Burmese
// dictionary word ("ครับ555" — "555" is Thai for hahaha — or "iPhoneของ"), so
// a word-like segment is cut into runs of unspaced-script letters (marks and
// joiners stay with their letter) and everything else. An unspaced run is a
// word; the rest goes through the letters-only regex, exactly as spaced text
// does, so "555" is never part of what a student captures.
function splitWordSegment(segment: string): CaptureToken[] {
  const out: CaptureToken[] = [];
  let run = "";
  let runUnspaced = false;
  const flush = () => {
    if (!run) return;
    if (runUnspaced) out.push({ text: run, isWord: true });
    else out.push(...tokenizeSpaced(run));
    run = "";
  };
  for (const char of segment) {
    const attaches = run !== "" && (COMBINING_MARK.test(char) || char === ZWNJ || char === ZWJ);
    const unspaced = UNSPACED_SCRIPT.test(char);
    if (run && !attaches && unspaced !== runUnspaced) flush();
    if (!run) runUnspaced = attaches ? runUnspaced : unspaced;
    run += char;
  }
  flush();
  return out;
}

// No segmenter: regex words, but every Han/Kana character inside a word run
// becomes its own token so a single character (or any span) is selectable. A
// Thai/Lao/Khmer/Burmese run has no such fallback and stays one token.
function tokenizeUnspacedFallback(text: string): CaptureToken[] {
  return tokenizeSpaced(text).flatMap((token) =>
    token.isWord && PER_CHARACTER_FALLBACK.test(token.text) ? splitUnspacedRun(token.text) : [token],
  );
}

function splitUnspacedRun(word: string): CaptureToken[] {
  const out: CaptureToken[] = [];
  let buffer = "";
  for (const char of word) {
    if (PER_CHARACTER_FALLBACK.test(char)) {
      if (buffer) out.push({ text: buffer, isWord: true });
      buffer = "";
      out.push({ text: char, isWord: true });
    } else {
      buffer += char;
    }
  }
  if (buffer) out.push({ text: buffer, isWord: true });
  return out;
}
